import React from 'react';
import { Card, Badge } from '@/components/ui';
import { CheckCircle, AlertTriangle, XCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { formatPercent, formatPercentTrend } from '@/utils/formatters';

interface CategoryCompliance {
  business: {
    actual: number;
    target: number;
    status: 'compliant' | 'non_compliant';
    trend: 'up' | 'down' | 'stable';
  };
  tech: {
    actual: number;
    target: number;
    status: 'compliant' | 'non_compliant';
    trend: 'up' | 'down' | 'stable';
  };
  support: {
    actual: number;
    target: number;
    status: 'compliant' | 'non_compliant';
    trend: 'up' | 'down' | 'stable';
  };
  overallCompliance: number;
  complianceScore: number;
}

interface CategoryComplianceMonitoringProps {
  categoryCompliance: CategoryCompliance | null;
  isLoading: boolean;
}

const CategoryComplianceMonitoring: React.FC<CategoryComplianceMonitoringProps> = ({
  categoryCompliance,
  isLoading,
}) => {
  const getComplianceIcon = (status: 'compliant' | 'non_compliant') => {
    return status === 'compliant' ? (
      <CheckCircle className="w-5 h-5 text-status-success" />
    ) : (
      <XCircle className="w-5 h-5 text-status-error" />
    );
  };

  const getComplianceBadge = (status: 'compliant' | 'non_compliant') => {
    return status === 'compliant' ? (
      <Badge variant="success">Compliant</Badge>
    ) : (
      <Badge variant="danger">Non-Compliant</Badge>
    );
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-status-success" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-status-error" />;
      default:
        return <div className="w-4 h-4" />;
    }
  };

  const getVarianceText = (actual: number, target: number) => {
    const variance = actual - target;
    return formatPercentTrend(variance);
  };

  const getVarianceColor = (actual: number, target: number) => {
    const variance = Math.abs(actual - target);
    if (variance <= 2) return 'text-status-success';
    if (variance <= 5) return 'text-status-warning';
    return 'text-status-error';
  };

  const categories = [
    {
      key: 'business' as const,
      label: 'Business',
      description: 'Customer-facing features',
      color: 'text-accent-blue',
      bgColor: 'bg-blue-500/10',
      barColor: 'bg-blue-500',
    },
    {
      key: 'tech' as const,
      label: 'Technology',
      description: 'Infrastructure & platform',
      color: 'text-sky-400',
      bgColor: 'bg-sky-500/10',
      barColor: 'bg-sky-500',
    },
    {
      key: 'support' as const,
      label: 'Support',
      description: 'Operations & maintenance',
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      barColor: 'bg-orange-500',
    },
  ];

  if (isLoading) {
    return (
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-muted/50 rounded w-1/2 mb-4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-muted/50 rounded w-3/4"></div>
                <div className="h-2 bg-muted/50 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (!categoryCompliance) {
    return (
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Category Compliance</h3>
          <div className="text-center text-muted-foreground py-8">
            No category compliance data available
          </div>
        </div>
      </Card>
    );
  }

  const overallCompliant = categoryCompliance.overallCompliance >= 80;

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Category Compliance</h3>
            <p className="text-sm text-muted-foreground">
              Target: 75% Business, 15% Tech, 10% Support
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {overallCompliant ? (
              <CheckCircle className="w-6 h-6 text-status-success" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-status-warning" />
            )}
            <Badge variant={overallCompliant ? 'success' : 'warning'}>
              {categoryCompliance.complianceScore}/3 Compliant
            </Badge>
          </div>
        </div>

        {/* Overall Compliance Score */}
        <div className="bg-muted rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Overall Compliance Score</h4>
              <p className="text-sm text-muted-foreground">
                Portfolio balance across all categories
              </p>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold ${overallCompliant ? 'text-status-success' : 'text-status-warning'}`}>
                {formatPercent(categoryCompliance.overallCompliance)}
              </div>
              <div className="text-sm text-muted-foreground">Compliance Rate</div>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="space-y-6">
          {categories.map((category) => {
            const data = categoryCompliance[category.key];
            
            return (
              <div key={category.key} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${category.bgColor}`}>
                      <div className={`w-3 h-3 rounded-full ${category.barColor}`} />
                    </div>
                    <div>
                      <h4 className={`font-medium ${category.color}`}>{category.label}</h4>
                      <p className="text-xs text-muted-foreground">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getTrendIcon(data.trend)}
                    {getComplianceIcon(data.status)}
                    {getComplianceBadge(data.status)}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Actual: {formatPercent(data.actual)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      Target: {data.target}%
                    </span>
                    <span className={`text-sm font-medium ${getVarianceColor(data.actual, data.target)}`}>
                      {getVarianceText(data.actual, data.target)}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative">
                    <div className="w-full bg-muted/50 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-300 ${category.barColor}`}
                        style={{ width: `${Math.min(data.actual, 100)}%` }}
                      />
                    </div>
                    {/* Target indicator */}
                    <div
                      className="absolute top-0 w-0.5 h-3 bg-white"
                      style={{ left: `${data.target}%` }}
                    />
                  </div>

                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0%</span>
                    <span>Target: {data.target}%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Compliance Recommendations */}
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="font-medium text-foreground mb-4">Compliance Recommendations</h4>
          
          <div className="space-y-3">
            {Object.entries(categoryCompliance).map(([key, data]) => {
              if (key === 'overallCompliance' || key === 'complianceScore' || typeof data !== 'object') return null;
              
              const categoryData = data as any;
              if (categoryData.status === 'non_compliant') {
                const variance = categoryData.actual - categoryData.target;
                const categoryInfo = categories.find(c => c.key === key);
                
                return (
                  <div key={key} className="p-3 bg-status-warning/10 border border-yellow-500/20 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-status-warning" />
                      <span className="text-sm font-medium text-status-warning">
                        {categoryInfo?.label} Category Alert
                      </span>
                    </div>
                    <p className="text-xs text-foreground">
                      {variance > 0 ? 'Over-allocated' : 'Under-allocated'} by {formatPercent(Math.abs(variance))}.
                      {variance > 0 ? ' Consider moving some capacity to other categories.' : ' Consider increasing allocation to meet targets.'}
                    </p>
                  </div>
                );
              }
              return null;
            })}

            {categoryCompliance.complianceScore === 3 && (
              <div className="p-3 bg-status-success/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center space-x-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-status-success" />
                  <span className="text-sm font-medium text-status-success">
                    All Categories Compliant
                  </span>
                </div>
                <p className="text-xs text-foreground">
                  Portfolio allocation is well-balanced across all categories. 
                  Continue monitoring to maintain compliance.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default CategoryComplianceMonitoring;